import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  buildModuleSummaries,
  clamp,
  fetchCourseManifest,
  formatClock,
  formatDuration,
  loadCourseText,
  parseWebVtt,
  resolveCourseAsset,
  slideModuleLabel,
} from "./course";
import {
  loadProgress,
  restartProgress,
  saveProgress,
  type GuidedProgress,
} from "./storage";
import type {
  AppView,
  CaptionCue,
  CourseManifest,
  CourseSlide,
  ModuleSummary,
} from "./types";

const PLAYBACK_SPEEDS = [0.75, 1, 1.25, 1.5, 2] as const;

function uniqueSlideIds(values: number[]): number[] {
  return [...new Set(values)].sort((left, right) => left - right);
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(
    target.closest(
      "button, a, input, select, textarea, audio, video, [contenteditable='true'], [role='slider']",
    ),
  );
}

interface CourseOutlineProps {
  modules: ModuleSummary[];
  completedSlideIds: number[];
  activeModule?: string;
  courseCompleted: boolean;
}

function CourseOutline({
  modules,
  completedSlideIds,
  activeModule,
  courseCompleted,
}: CourseOutlineProps) {
  const completedSet = useMemo(
    () => new Set(completedSlideIds),
    [completedSlideIds],
  );

  return (
    <ol className="module-list" aria-label="Course modules">
      {modules.map((module, index) => {
        const moduleComplete =
          courseCompleted ||
          (module.slideIds.length > 0 &&
            module.slideIds.every((slideId) => completedSet.has(slideId)));
        const isActive = activeModule === module.name;

        return (
          <li
            className={[
              "module-list__item",
              isActive ? "is-active" : "",
              moduleComplete ? "is-complete" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            key={`${module.name}-${index}`}
          >
            <span className="module-list__index" aria-hidden="true">
              {moduleComplete ? "✓" : String(index + 1).padStart(2, "0")}
            </span>
            <span>
              <span className="module-list__eyebrow">
                Module {index + 1}
              </span>
              <span className="module-list__name">{module.name}</span>
              <span className="module-list__status">
                {moduleComplete
                  ? "Completed"
                  : isActive
                    ? "Current module"
                    : `${module.slideIds.length} ${
                        module.slideIds.length === 1 ? "slide" : "slides"
                      }`}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

interface OverviewProps {
  manifest: CourseManifest;
  modules: ModuleSummary[];
  progress: GuidedProgress;
  onStart: () => void;
  onResume: () => void;
  onReviewCompletion: () => void;
  onTranscript: () => void;
}

function Overview({
  manifest,
  modules,
  progress,
  onStart,
  onResume,
  onReviewCompletion,
  onTranscript,
}: OverviewProps) {
  const resumable = progress.started && !progress.courseCompleted;
  const completedCount = progress.completedSlideIds.filter((id) =>
    manifest.slides.some((slide) => slide.id === id),
  ).length;

  return (
    <main className="overview" id="main-content" data-testid="guided-landing">
      <section className="overview__hero" aria-labelledby="course-title">
        <div className="overview__intro">
          <p className="eyebrow">Learner-controlled training</p>
          <h1 id="course-title">{manifest.courseTitle}</h1>
          <p className="overview__subtitle">
            {manifest.subtitle ??
              "A contextual governance framework for adaptive AI operations"}
          </p>
          <p className="overview__candidate">
            <strong>{manifest.candidate}</strong>
            <span>{manifest.credential}</span>
          </p>

          <dl className="course-facts" aria-label="Course facts">
            <div>
              <dt>Format</dt>
              <dd>Guided, slide by slide</dd>
            </div>
            <div>
              <dt>Course length</dt>
              <dd>{formatDuration(manifest.totalDurationMs)}</dd>
            </div>
            <div>
              <dt>Structure</dt>
              <dd>
                {manifest.totalSlides} slides · {modules.length} modules
              </dd>
            </div>
          </dl>

          {progress.courseCompleted ? (
            <div className="resume-card" data-testid="completed-overview">
              <div>
                <p className="resume-card__label">Completed on this device</p>
                <p>You can review the completion record or begin again.</p>
              </div>
              <button
                className="button button--secondary"
                data-testid="review-completion"
                onClick={onReviewCompletion}
                type="button"
              >
                Review completion
              </button>
            </div>
          ) : resumable ? (
            <div className="resume-card" data-testid="resume-offer">
              <div>
                <p className="resume-card__label">Continue where you left off</p>
                <p>
                  Slide {progress.lastSlideId} · {completedCount} of{" "}
                  {manifest.totalSlides} slides completed
                </p>
              </div>
              <button
                className="button button--primary"
                data-testid="resume-training"
                onClick={onResume}
                type="button"
              >
                Resume Training
              </button>
            </div>
          ) : null}

          <div className="overview__actions">
            <button
              className="button button--primary button--large"
              data-testid={progress.started ? "start-again" : "start-training"}
              onClick={onStart}
              type="button"
            >
              {progress.started ? "Start Again" : "Start Training"}
            </button>
            <button
              className="button button--quiet"
              onClick={onTranscript}
              type="button"
            >
              Read Full Transcript
            </button>
          </div>
          <p className="overview__playback-note">
            Narration begins only after you choose to start or resume. Slides
            never advance automatically.
          </p>
        </div>

        <aside className="overview__modules" aria-labelledby="module-heading">
          <div className="section-heading">
            <p className="eyebrow">Course map</p>
            <h2 id="module-heading">Six modules, one guided sequence</h2>
          </div>
          <CourseOutline
            completedSlideIds={progress.completedSlideIds}
            courseCompleted={progress.courseCompleted}
            modules={modules}
          />
        </aside>
      </section>

      <section className="objectives" aria-labelledby="objectives-heading">
        <div className="section-heading">
          <p className="eyebrow">Learning objectives</p>
          <h2 id="objectives-heading">By the end of this training</h2>
        </div>
        <ul className="objectives__grid">
          {manifest.learningObjectives.map((objective, index) => (
            <li key={objective}>
              <span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
              <p>{objective}</p>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

interface AudioControlsProps {
  slide: CourseSlide;
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  isBuffering: boolean;
  muted: boolean;
  volume: number;
  playbackSpeed: number;
  captionsEnabled: boolean;
  transcriptOpen: boolean;
  onPlayToggle: () => void;
  onReplay: () => void;
  onSeek: (value: number) => void;
  onMuteToggle: () => void;
  onVolume: (value: number) => void;
  onPlaybackSpeed: (value: number) => void;
  onCaptionsToggle: () => void;
  onTranscriptToggle: () => void;
}

function AudioControls({
  slide,
  currentTime,
  duration,
  isPlaying,
  isBuffering,
  muted,
  volume,
  playbackSpeed,
  captionsEnabled,
  transcriptOpen,
  onPlayToggle,
  onReplay,
  onSeek,
  onMuteToggle,
  onVolume,
  onPlaybackSpeed,
  onCaptionsToggle,
  onTranscriptToggle,
}: AudioControlsProps) {
  const effectiveDuration = duration || slide.durationMs / 1000;
  const seekMaximum = Math.max(effectiveDuration, 0.1);

  return (
    <div
      className="audio-controls"
      role="group"
      aria-label={`Narration controls for slide ${slide.id}`}
      data-testid="audio-controls"
    >
      <div className="audio-controls__primary">
        <button
          aria-keyshortcuts="Space"
          className="control-button control-button--primary"
          data-testid="audio-play-toggle"
          onClick={onPlayToggle}
          type="button"
        >
          {isPlaying ? "Pause narration" : "Play narration"}
        </button>
        <button
          className="control-button"
          data-testid="audio-replay"
          onClick={onReplay}
          type="button"
        >
          Replay
        </button>
        <span className="buffering-status" aria-live="polite">
          {isBuffering ? "Buffering…" : ""}
        </span>
      </div>

      <div className="timeline-control">
        <label className="sr-only" htmlFor="narration-seek">
          Seek within slide {slide.id} narration
        </label>
        <input
          aria-valuetext={`${formatClock(currentTime)} of ${formatClock(
            effectiveDuration,
          )}`}
          data-testid="audio-seek"
          id="narration-seek"
          max={seekMaximum}
          min="0"
          onChange={(event) => onSeek(Number(event.target.value))}
          step="0.1"
          type="range"
          value={clamp(currentTime, 0, seekMaximum)}
        />
        <div className="timeline-control__time" aria-live="off">
          <span>{formatClock(currentTime)}</span>
          <span>{formatClock(effectiveDuration)}</span>
        </div>
      </div>

      <div className="audio-controls__secondary">
        <div className="volume-control">
          <button
            aria-label={muted ? "Unmute narration" : "Mute narration"}
            aria-keyshortcuts="M"
            aria-pressed={muted}
            className="control-button"
            data-testid="audio-mute"
            onClick={onMuteToggle}
            type="button"
          >
            {muted ? "Unmute" : "Mute"}
          </button>
          <label className="sr-only" htmlFor="narration-volume">
            Narration volume
          </label>
          <input
            aria-valuetext={`${Math.round((muted ? 0 : volume) * 100)} percent`}
            data-testid="audio-volume"
            id="narration-volume"
            max="1"
            min="0"
            onChange={(event) => onVolume(Number(event.target.value))}
            step="0.05"
            type="range"
            value={muted ? 0 : volume}
          />
        </div>

        <label className="speed-control">
          <span>Speed</span>
          <select
            data-testid="audio-speed"
            onChange={(event: ChangeEvent<HTMLSelectElement>) =>
              onPlaybackSpeed(Number(event.target.value))
            }
            value={playbackSpeed}
          >
            {PLAYBACK_SPEEDS.map((speed) => (
              <option key={speed} value={speed}>
                {speed}×
              </option>
            ))}
          </select>
        </label>

        <button
          aria-keyshortcuts="C"
          aria-pressed={captionsEnabled}
          className="control-button"
          data-testid="captions-toggle"
          onClick={onCaptionsToggle}
          type="button"
        >
          Captions {captionsEnabled ? "on" : "off"}
        </button>
        <button
          aria-controls="slide-transcript"
          aria-expanded={transcriptOpen}
          className="control-button"
          data-testid="transcript-toggle"
          onClick={onTranscriptToggle}
          type="button"
        >
          {transcriptOpen ? "Hide transcript" : "Show transcript"}
        </button>
      </div>
    </div>
  );
}

interface GuidedPlayerProps {
  manifest: CourseManifest;
  modules: ModuleSummary[];
  slideIndex: number;
  progress: GuidedProgress;
  setProgress: (
    updater: GuidedProgress | ((previous: GuidedProgress) => GuidedProgress),
  ) => void;
  onNavigate: (direction: "back" | "next") => void;
  onComplete: () => void;
  onOverview: () => void;
  autoPlayRequestedRef: React.MutableRefObject<boolean>;
  desiredStartTimeRef: React.MutableRefObject<number>;
}

function GuidedPlayer({
  manifest,
  modules,
  slideIndex,
  progress,
  setProgress,
  onNavigate,
  onComplete,
  onOverview,
  autoPlayRequestedRef,
  desiredStartTimeRef,
}: GuidedPlayerProps) {
  const slide = manifest.slides[slideIndex];
  const isFinalSlide = slideIndex === manifest.slides.length - 1;
  const currentModule = slideModuleLabel(slide, manifest.modules);
  const audioRef = useRef<HTMLAudioElement>(null);
  const lastSavedPositionRef = useRef(0);
  const [currentTime, setCurrentTime] = useState(
    progress.audioPositions[String(slide.id)] ?? 0,
  );
  const [duration, setDuration] = useState(slide.durationMs / 1000);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [narrationEnded, setNarrationEnded] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [audioError, setAudioError] = useState("");
  const [audioSource, setAudioSource] = useState("");
  const [audioAttempt, setAudioAttempt] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [transcriptError, setTranscriptError] = useState("");
  const [captionCues, setCaptionCues] = useState<CaptionCue[]>([]);
  const [captionsLoading, setCaptionsLoading] = useState(true);
  const [announcement, setAnnouncement] = useState(
    `Slide ${slideIndex + 1} of ${manifest.totalSlides}: ${slide.title}`,
  );

  const completedCount = progress.completedSlideIds.filter((id) =>
    manifest.slides.some((manifestSlide) => manifestSlide.id === id),
  ).length;
  const percentage = Math.round(
    (completedCount / Math.max(manifest.totalSlides, 1)) * 100,
  );

  const savePosition = useCallback(
    (position: number) => {
      setProgress((previous) => ({
        ...previous,
        lastSlideId: slide.id,
        audioPositions: {
          ...previous.audioPositions,
          [String(slide.id)]: Math.max(0, position),
        },
      }));
    },
    [setProgress, slide.id],
  );

  const requestPlayback = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    setAutoplayBlocked(false);
    setAudioError("");

    try {
      await audio.play();
    } catch {
      setAutoplayBlocked(true);
      setIsPlaying(false);
      setAnnouncement(
        "Narration is ready. Choose Play narration to continue.",
      );
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = "";

    setAudioSource("");
    setAudioError("");
    setIsBuffering(true);

    void fetch(resolveCourseAsset(slide.audio), {
      cache: "force-cache",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Narration request returned HTTP ${response.status}.`);
        }
        return response.blob();
      })
      .then((blob) => {
        if (controller.signal.aborted) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setAudioSource(objectUrl);
      })
      .catch((error: unknown) => {
        if (
          controller.signal.aborted ||
          (error instanceof DOMException && error.name === "AbortError")
        ) {
          return;
        }
        setAudioError(
          "Narration could not be loaded. Check your connection and try again.",
        );
        setIsBuffering(false);
        setIsPlaying(false);
      });

    return () => {
      controller.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [audioAttempt, slide.audio]);

  useEffect(() => {
    const controller = new AbortController();
    setTranscript("");
    setTranscriptError("");
    setCaptionCues([]);
    setCaptionsLoading(true);

    void Promise.allSettled([
      loadCourseText(slide.transcript, controller.signal),
      loadCourseText(slide.caption, controller.signal),
    ])
      .then(([transcriptResult, captionResult]) => {
        if (controller.signal.aborted) {
          return;
        }

        if (transcriptResult.status === "fulfilled") {
          setTranscript(transcriptResult.value);
        } else {
          setTranscriptError(
            transcriptResult.reason instanceof Error
              ? transcriptResult.reason.message
              : "The slide transcript could not be loaded.",
          );
        }

        if (captionResult.status === "fulfilled") {
          const loadedCaptions = captionResult.value;
          const parsedCues = parseWebVtt(loadedCaptions);
          setCaptionCues(
            parsedCues.length > 0
              ? parsedCues
              : loadedCaptions
                ? [
                    {
                      start: 0,
                      end: Math.max(slide.durationMs / 1000, 0.1),
                      text: loadedCaptions,
                    },
                  ]
                : [],
          );
        } else {
          setCaptionCues([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setCaptionsLoading(false);
        }
      });

    return () => controller.abort();
  }, [slide.caption, slide.durationMs, slide.transcript]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    audio.pause();
    if (!audioSource) {
      audio.load();
      return;
    }

    setIsPlaying(false);
    setIsBuffering(true);
    setNarrationEnded(false);
    setAutoplayBlocked(false);
    setAudioError("");
    setDuration(slide.durationMs / 1000);
    setCurrentTime(desiredStartTimeRef.current);
    lastSavedPositionRef.current = desiredStartTimeRef.current;
    setAnnouncement(
      `Slide ${slideIndex + 1} of ${manifest.totalSlides}: ${slide.title}. ${currentModule}.`,
    );
    audio.load();
  }, [audioSource]);

  useEffect(() => {
    const handlePageHide = () => {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }

      saveProgress({
        ...progress,
        lastSlideId: slide.id,
        audioPositions: {
          ...progress.audioPositions,
          [String(slide.id)]: Math.max(0, audio.currentTime),
        },
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        handlePageHide();
      }
    };

    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [progress, slide.id]);

  useEffect(() => {
    const handleKeyboard = (event: globalThis.KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        isInteractiveTarget(event.target)
      ) {
        return;
      }

      if (event.key === "ArrowLeft" && slideIndex > 0) {
        event.preventDefault();
        onNavigate("back");
      } else if (event.key === "ArrowRight" && !isFinalSlide) {
        event.preventDefault();
        onNavigate("next");
      } else if (event.key === " ") {
        event.preventDefault();
        const audio = audioRef.current;
        if (audio?.paused) {
          void requestPlayback();
        } else {
          audio?.pause();
        }
      } else if (event.key.toLowerCase() === "m") {
        event.preventDefault();
        setProgress((previous) => ({ ...previous, muted: !previous.muted }));
      } else if (event.key.toLowerCase() === "c") {
        event.preventDefault();
        setProgress((previous) => ({
          ...previous,
          captionsEnabled: !previous.captionsEnabled,
        }));
      }
    };

    window.addEventListener("keydown", handleKeyboard);
    return () => window.removeEventListener("keydown", handleKeyboard);
  }, [
    isFinalSlide,
    onNavigate,
    requestPlayback,
    setProgress,
    slideIndex,
  ]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    audio.volume = progress.volume;
    audio.muted = progress.muted;
    audio.playbackRate = progress.playbackSpeed;
  }, [progress.muted, progress.playbackSpeed, progress.volume]);

  const activeCaption = useMemo(
    () =>
      captionCues.find(
        (cue) => currentTime >= cue.start && currentTime < cue.end,
      )?.text ?? "",
    [captionCues, currentTime],
  );

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const measuredDuration =
      Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : slide.durationMs / 1000;
    const restoredPosition = clamp(
      desiredStartTimeRef.current,
      0,
      Math.max(measuredDuration - 0.05, 0),
    );

    setDuration(measuredDuration);
    audio.currentTime = restoredPosition;
    setCurrentTime(restoredPosition);
    setIsBuffering(false);

    if (autoPlayRequestedRef.current) {
      autoPlayRequestedRef.current = false;
      void requestPlayback();
    }
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const position = audio.currentTime;
    setCurrentTime(position);

    if (Math.abs(position - lastSavedPositionRef.current) >= 1) {
      lastSavedPositionRef.current = position;
      savePosition(position);
    }
  };

  const handlePlayToggle = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (audio.paused) {
      if (audio.ended || audio.currentTime >= duration - 0.05) {
        audio.currentTime = 0;
        setCurrentTime(0);
        setNarrationEnded(false);
      }
      void requestPlayback();
    } else {
      audio.pause();
    }
  };

  const handleReplay = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    audio.currentTime = 0;
    setCurrentTime(0);
    setNarrationEnded(false);
    savePosition(0);
    void requestPlayback();
  };

  const handleSeek = (value: number) => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const nextTime = clamp(value, 0, duration || slide.durationMs / 1000);
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
    setNarrationEnded(false);
    lastSavedPositionRef.current = nextTime;
    savePosition(nextTime);
  };

  const handleVolume = (value: number) => {
    const nextVolume = clamp(value, 0, 1);
    setProgress((previous) => ({
      ...previous,
      volume: nextVolume,
      muted: nextVolume === 0 ? previous.muted : false,
    }));
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setIsBuffering(false);
    setNarrationEnded(true);
    setCurrentTime(duration);
    setProgress((previous) => ({
      ...previous,
      completedSlideIds: uniqueSlideIds([
        ...previous.completedSlideIds,
        slide.id,
      ]),
      audioPositions: {
        ...previous.audioPositions,
        [String(slide.id)]: duration,
      },
    }));
    setAnnouncement(
      "Narration complete. The slide will remain here until you choose Next.",
    );
  };

  const handleNavigationClick = (direction: "back" | "next") => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    onNavigate(direction);
  };

  const handleCompleteClick = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
    onComplete();
  };

  const handleOverview = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      savePosition(audio.currentTime);
    }
    onOverview();
  };

  return (
    <main className="course-view" id="main-content" data-testid="course-player">
      <div className="sr-only" aria-atomic="true" aria-live="polite">
        {announcement}
      </div>

      <header className="course-header">
        <div>
          <p className="eyebrow">Guided thesis training</p>
          <p className="course-header__title">{manifest.courseTitle}</p>
        </div>
        <button
          className="button button--quiet button--compact"
          onClick={handleOverview}
          type="button"
        >
          Return to Overview
        </button>
      </header>

      <section className="course-progress" aria-label="Course progress">
        <div>
          <span data-testid="module-name">{currentModule}</span>
          <span data-testid="slide-number">
            Slide {slideIndex + 1} of {manifest.totalSlides}
          </span>
        </div>
        <progress
          aria-label={`${percentage} percent of slides completed`}
          max="100"
          value={percentage}
        >
          {percentage}%
        </progress>
        <p>{completedCount} slides completed</p>
      </section>

      <div className="player-layout">
        <div className="player-column">
          <section className="slide-card" aria-labelledby="active-slide-title">
            <header className="slide-card__header">
              <p>{currentModule}</p>
              <div className="slide-card__heading-row">
                <h1 id="active-slide-title">{slide.title}</h1>
                <a
                  aria-label={`Open slide ${slide.id} full size in a new tab`}
                  className="slide-card__full-size"
                  href={resolveCourseAsset(slide.image)}
                  rel="noreferrer"
                  target="_blank"
                >
                  Open slide full size
                </a>
              </div>
            </header>

            <figure className="slide-stage">
              <img
                alt={slide.altText || slide.title}
                data-testid="slide-image"
                decoding="async"
                draggable="false"
                src={resolveCourseAsset(slide.image)}
              />
              {progress.captionsEnabled ? (
                <figcaption
                  aria-live="polite"
                  className="caption-rail"
                  data-testid="caption-display"
                >
                  {captionsLoading
                    ? "Loading captions…"
                    : activeCaption || "Captions will appear with the narration."}
                </figcaption>
              ) : null}
            </figure>

            <nav
              aria-label="Slide navigation"
              className="slide-navigation"
              data-testid="slide-navigation"
            >
              <button
                aria-keyshortcuts="ArrowLeft"
                className="button button--secondary button--large"
                data-testid="back-button"
                disabled={slideIndex === 0}
                onClick={() => handleNavigationClick("back")}
                type="button"
              >
                Back
              </button>
              {isFinalSlide ? (
                <button
                  className="button button--primary button--large"
                  data-testid="complete-training"
                  onClick={handleCompleteClick}
                  type="button"
                >
                  Complete Training
                </button>
              ) : (
                <button
                  aria-keyshortcuts="ArrowRight"
                  className="button button--primary button--large"
                  data-testid="next-button"
                  onClick={() => handleNavigationClick("next")}
                  type="button"
                >
                  Next
                </button>
              )}
            </nav>

            <audio
              aria-hidden="true"
              data-audio-file={slide.audio}
              data-audio-source-ready={audioSource ? "true" : "false"}
              data-testid="slide-audio"
              onCanPlay={() => setIsBuffering(false)}
              onEnded={handleAudioEnded}
              onError={() => {
                if (!audioSource) {
                  return;
                }
                setAudioError(
                  "Narration could not be loaded. Check your connection and try again.",
                );
                setIsBuffering(false);
                setIsPlaying(false);
              }}
              onLoadedMetadata={handleLoadedMetadata}
              onPause={() => {
                const audio = audioRef.current;
                setIsPlaying(false);
                if (audio) {
                  savePosition(audio.currentTime);
                  if (
                    audio.currentTime > 0 &&
                    !audio.ended &&
                    !autoPlayRequestedRef.current
                  ) {
                    setAnnouncement("Narration paused.");
                  }
                }
              }}
              onPlaying={() => {
                setIsPlaying(true);
                setIsBuffering(false);
                setAutoplayBlocked(false);
                setAnnouncement("Narration playing.");
              }}
              onTimeUpdate={handleTimeUpdate}
              onWaiting={() => setIsBuffering(true)}
              preload="metadata"
              ref={audioRef}
              src={audioSource || undefined}
              tabIndex={-1}
            />

            {autoplayBlocked ? (
              <div
                className="media-message media-message--notice"
                data-testid="autoplay-fallback"
                role="status"
              >
                <p>Your browser paused automatic narration.</p>
                <button
                  className="button button--primary"
                  onClick={() => void requestPlayback()}
                  type="button"
                >
                  Play narration
                </button>
              </div>
            ) : null}

            {audioError ? (
              <div
                className="media-message media-message--error"
                data-testid="audio-error"
                role="alert"
              >
                <p>{audioError}</p>
                <button
                  className="button button--secondary"
                  onClick={() => {
                    setAudioError("");
                    setAudioAttempt((attempt) => attempt + 1);
                  }}
                  type="button"
                >
                  Retry narration
                </button>
              </div>
            ) : null}

            <AudioControls
              captionsEnabled={progress.captionsEnabled}
              currentTime={currentTime}
              duration={duration}
              isBuffering={isBuffering}
              isPlaying={isPlaying}
              muted={progress.muted}
              onCaptionsToggle={() =>
                setProgress((previous) => ({
                  ...previous,
                  captionsEnabled: !previous.captionsEnabled,
                }))
              }
              onMuteToggle={() =>
                setProgress((previous) => ({
                  ...previous,
                  muted: !previous.muted,
                }))
              }
              onPlaybackSpeed={(playbackSpeed) =>
                setProgress((previous) => ({ ...previous, playbackSpeed }))
              }
              onPlayToggle={handlePlayToggle}
              onReplay={handleReplay}
              onSeek={handleSeek}
              onTranscriptToggle={() =>
                setProgress((previous) => ({
                  ...previous,
                  transcriptOpen: !previous.transcriptOpen,
                }))
              }
              onVolume={handleVolume}
              playbackSpeed={progress.playbackSpeed}
              slide={slide}
              transcriptOpen={progress.transcriptOpen}
              volume={progress.volume}
            />

            {narrationEnded ? (
              <p
                className="narration-complete"
                data-testid="narration-complete"
                role="status"
              >
                Narration complete — click Next to continue.
              </p>
            ) : null}

            <p className="keyboard-hint">
              Keyboard: Left Arrow for Back, Right Arrow for Next, Space to
              play or pause, M to mute, C for captions. Shortcuts pause while a
              control has focus.
            </p>
          </section>
        </div>

        <aside className="support-column" aria-label="Course support">
          {progress.transcriptOpen ? (
            <section
              className="transcript-panel"
              data-testid="slide-transcript"
              id="slide-transcript"
            >
              <div className="section-heading section-heading--compact">
                <p className="eyebrow">Speaker notes</p>
                <h2>Slide transcript</h2>
              </div>
              {transcriptError ? (
                <p className="inline-error" role="alert">
                  {transcriptError}
                </p>
              ) : transcript ? (
                <div className="transcript-copy">
                  {transcript.split(/\n{2,}/).map((paragraph, index) => (
                    <p key={`${slide.id}-${index}`}>{paragraph}</p>
                  ))}
                </div>
              ) : (
                <p className="loading-copy">Loading transcript…</p>
              )}
            </section>
          ) : (
            <section className="transcript-panel transcript-panel--closed">
              <p>The transcript is hidden.</p>
              <button
                className="button button--quiet"
                onClick={() =>
                  setProgress((previous) => ({
                    ...previous,
                    transcriptOpen: true,
                  }))
                }
                type="button"
              >
                Show slide transcript
              </button>
            </section>
          )}

          <section className="outline-panel" aria-labelledby="outline-heading">
            <div className="section-heading section-heading--compact">
              <p className="eyebrow">Course outline</p>
              <h2 id="outline-heading">Your progress</h2>
            </div>
            <CourseOutline
              activeModule={currentModule}
              completedSlideIds={progress.completedSlideIds}
              courseCompleted={progress.courseCompleted}
              modules={modules}
            />
          </section>
        </aside>
      </div>
    </main>
  );
}

interface CompletionProps {
  manifest: CourseManifest;
  modules: ModuleSummary[];
  onReviewPrevious: () => void;
  onRestart: () => void;
  onTranscript: () => void;
  onOverview: () => void;
}

function Completion({
  manifest,
  modules,
  onReviewPrevious,
  onRestart,
  onTranscript,
  onOverview,
}: CompletionProps) {
  return (
    <main
      className="completion"
      id="main-content"
      data-testid="completion-screen"
    >
      <div className="completion__mark" aria-hidden="true">
        ✓
      </div>
      <p className="eyebrow">Course complete</p>
      <h1>Training completed</h1>
      <p className="completion__title">{manifest.courseTitle}</p>
      <blockquote>{manifest.conclusion}</blockquote>

      <section className="completion__modules" aria-labelledby="completed-modules">
        <h2 id="completed-modules">All course modules completed</h2>
        <CourseOutline
          completedSlideIds={manifest.slides.map((slide) => slide.id)}
          courseCompleted
          modules={modules}
        />
      </section>

      <div className="completion__actions">
        <button
          className="button button--secondary"
          onClick={onReviewPrevious}
          type="button"
        >
          Review Previous Slide
        </button>
        <button
          className="button button--primary"
          data-testid="restart-course"
          onClick={onRestart}
          type="button"
        >
          Restart Course
        </button>
        <button
          className="button button--secondary"
          onClick={onTranscript}
          type="button"
        >
          Read Full Transcript
        </button>
        <button className="button button--quiet" onClick={onOverview} type="button">
          Return to Overview
        </button>
      </div>

      <div className="sr-only" aria-live="assertive">
        Training completed.
      </div>
    </main>
  );
}

interface FullTranscriptProps {
  manifest: CourseManifest;
  onBack: () => void;
}

function FullTranscript({ manifest, onBack }: FullTranscriptProps) {
  const [transcripts, setTranscripts] = useState<Record<number, string>>({});
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setError("");

    void Promise.allSettled(
      manifest.slides.map(async (slide) => ({
        id: slide.id,
        text: await loadCourseText(slide.transcript, controller.signal),
      })),
    )
      .then((values) => {
        if (controller.signal.aborted) {
          return;
        }

        const fulfilled = values.flatMap((result) =>
          result.status === "fulfilled" ? [result.value] : [],
        );
        setTranscripts(
          Object.fromEntries(fulfilled.map(({ id, text }) => [id, text])),
        );

        if (fulfilled.length !== values.length) {
          setError(
            "One or more transcript sections could not be loaded. Available sections are shown below.",
          );
        }
      });

    return () => controller.abort();
  }, [manifest.slides]);

  return (
    <main
      className="full-transcript"
      id="main-content"
      data-testid="full-transcript"
    >
      <header className="full-transcript__header">
        <div>
          <p className="eyebrow">Accessible course reference</p>
          <h1>Full course transcript</h1>
          <p>{manifest.courseTitle}</p>
        </div>
        <button
          className="button button--secondary"
          onClick={onBack}
          type="button"
        >
          Go back
        </button>
      </header>

      {error ? (
        <p className="inline-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="full-transcript__content">
        {manifest.slides.map((slide, index) => (
          <section key={slide.id} aria-labelledby={`transcript-slide-${slide.id}`}>
            <p className="eyebrow">
              Slide {index + 1} of {manifest.totalSlides} ·{" "}
              {slideModuleLabel(slide, manifest.modules)}
            </p>
            <h2 id={`transcript-slide-${slide.id}`}>{slide.title}</h2>
            {transcripts[slide.id] ? (
              transcripts[slide.id].split(/\n{2,}/).map((paragraph, paragraphIndex) => (
                <p key={`${slide.id}-${paragraphIndex}`}>{paragraph}</p>
              ))
            ) : (
              <p className="loading-copy">Loading transcript…</p>
            )}
          </section>
        ))}
      </div>
    </main>
  );
}

export default function App() {
  const [manifest, setManifest] = useState<CourseManifest | null>(null);
  const [manifestError, setManifestError] = useState("");
  const [manifestAttempt, setManifestAttempt] = useState(0);
  const [view, setView] = useState<AppView>("overview");
  const [transcriptReturnView, setTranscriptReturnView] =
    useState<AppView>("overview");
  const [slideIndex, setSlideIndex] = useState(0);
  const [progress, setProgress] = useState<GuidedProgress>(() => loadProgress());
  const autoPlayRequestedRef = useRef(false);
  const desiredStartTimeRef = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    setManifestError("");

    void fetchCourseManifest(controller.signal)
      .then(setManifest)
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setManifestError(
          error instanceof Error
            ? error.message
            : "The course could not be loaded.",
        );
      });

    return () => controller.abort();
  }, [manifestAttempt]);

  useEffect(() => {
    saveProgress(progress);
  }, [progress]);

  const modules = useMemo(
    () => (manifest ? buildModuleSummaries(manifest) : []),
    [manifest],
  );

  const enterCourse = useCallback(
    (
      targetIndex: number,
      startTime: number,
      requestAutoplay: boolean,
      preserveCompletion = false,
    ) => {
      if (!manifest) {
        return;
      }

      const safeIndex = clamp(
        targetIndex,
        0,
        Math.max(manifest.slides.length - 1, 0),
      );
      const slide = manifest.slides[safeIndex];

      autoPlayRequestedRef.current = requestAutoplay;
      desiredStartTimeRef.current = Math.max(0, startTime);
      setSlideIndex(safeIndex);
      setProgress((previous) => ({
        ...previous,
        started: true,
        lastSlideId: slide.id,
        courseCompleted: preserveCompletion
          ? previous.courseCompleted
          : false,
        audioPositions: {
          ...previous.audioPositions,
          [String(slide.id)]: Math.max(0, startTime),
        },
      }));
      setView("course");
    },
    [manifest],
  );

  const handleStart = useCallback(() => {
    if (!manifest) {
      return;
    }

    const restarted = restartProgress(progress, manifest.slides[0].id);
    setProgress(restarted);
    enterCourse(0, 0, true);
  }, [enterCourse, manifest, progress]);

  const handleResume = useCallback(() => {
    if (!manifest) {
      return;
    }

    const resumeIndex = Math.max(
      0,
      manifest.slides.findIndex((slide) => slide.id === progress.lastSlideId),
    );
    const resumeSlide = manifest.slides[resumeIndex];
    const resumePosition =
      progress.audioPositions[String(resumeSlide.id)] ?? 0;
    enterCourse(resumeIndex, resumePosition, true);
  }, [enterCourse, manifest, progress.audioPositions, progress.lastSlideId]);

  const handleNavigate = useCallback(
    (direction: "back" | "next") => {
      if (!manifest) {
        return;
      }

      const increment = direction === "next" ? 1 : -1;
      const targetIndex = clamp(
        slideIndex + increment,
        0,
        manifest.slides.length - 1,
      );

      if (targetIndex === slideIndex) {
        return;
      }

      const currentSlide = manifest.slides[slideIndex];
      const targetSlide = manifest.slides[targetIndex];
      autoPlayRequestedRef.current = true;
      desiredStartTimeRef.current = 0;
      setProgress((previous) => ({
        ...previous,
        lastSlideId: targetSlide.id,
        completedSlideIds:
          direction === "next"
            ? uniqueSlideIds([
                ...previous.completedSlideIds,
                currentSlide.id,
              ])
            : previous.completedSlideIds,
        audioPositions: {
          ...previous.audioPositions,
          [String(currentSlide.id)]: 0,
          [String(targetSlide.id)]: 0,
        },
      }));
      setSlideIndex(targetIndex);
    },
    [manifest, slideIndex],
  );

  const handleComplete = useCallback(() => {
    if (!manifest || slideIndex !== manifest.slides.length - 1) {
      return;
    }

    setProgress((previous) => ({
      ...previous,
      courseCompleted: true,
      completedSlideIds: manifest.slides.map((slide) => slide.id),
      lastSlideId: manifest.slides[manifest.slides.length - 1].id,
    }));
    setView("completion");
  }, [manifest, slideIndex]);

  const openTranscript = (returnView: AppView) => {
    setTranscriptReturnView(returnView);
    setView("transcript");
  };

  if (!manifest && !manifestError) {
    return (
      <main className="load-state" id="main-content" data-testid="course-loading">
        <div className="load-state__indicator" aria-hidden="true" />
        <p className="eyebrow">Guided thesis training</p>
        <h1>Preparing your course</h1>
        <p role="status">Loading the course manifest and media map…</p>
      </main>
    );
  }

  if (manifestError || !manifest) {
    return (
      <main className="load-state" id="main-content" data-testid="course-error">
        <p className="eyebrow">Course unavailable</p>
        <h1>The training could not be opened</h1>
        <p role="alert">{manifestError}</p>
        <button
          className="button button--primary"
          onClick={() => setManifestAttempt((attempt) => attempt + 1)}
          type="button"
        >
          Try again
        </button>
      </main>
    );
  }

  if (view === "course") {
    return (
      <GuidedPlayer
        autoPlayRequestedRef={autoPlayRequestedRef}
        desiredStartTimeRef={desiredStartTimeRef}
        manifest={manifest}
        modules={modules}
        onComplete={handleComplete}
        onNavigate={handleNavigate}
        onOverview={() => setView("overview")}
        progress={progress}
        setProgress={setProgress}
        slideIndex={slideIndex}
      />
    );
  }

  if (view === "completion") {
    return (
      <Completion
        manifest={manifest}
        modules={modules}
        onOverview={() => setView("overview")}
        onRestart={handleStart}
        onReviewPrevious={() =>
          enterCourse(
            Math.max(manifest.slides.length - 2, 0),
            0,
            false,
            true,
          )
        }
        onTranscript={() => openTranscript("completion")}
      />
    );
  }

  if (view === "transcript") {
    return (
      <FullTranscript
        manifest={manifest}
        onBack={() =>
          setView(
            transcriptReturnView === "transcript"
              ? "overview"
              : transcriptReturnView,
          )
        }
      />
    );
  }

  return (
    <Overview
      manifest={manifest}
      modules={modules}
      onResume={handleResume}
      onReviewCompletion={() => setView("completion")}
      onStart={handleStart}
      onTranscript={() => openTranscript("overview")}
      progress={progress}
    />
  );
}
